import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { AimOutlined } from '@ant-design/icons';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import CustomButton from '../../common/Button';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Global styles for Google Places Autocomplete dropdown
const GlobalAutocompleteStyles = createGlobalStyle`
  .pac-container {
    z-index: 10000 !important;
    border-radius: 4px;
    margin-top: 4px;
    box-shadow: 0 1px 2px ${COLORS.grey.medium};
    font-family: inherit;
  }
  
  .pac-item {
    padding: 8px 12px;
    cursor: pointer;
    font-size: ${TEXT_SIZES.SM}px;
    line-height: 1.5;
  }
  
  .pac-item:hover {
    background-color: ${COLORS.white.primary};
  }
  
  .pac-item-query {
    font-size: ${TEXT_SIZES.SM}px;
    color: #000;
  }
  
  .pac-matched {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

interface Address {
  street: string;
  city: string;
  state: string;
  pincode: string;
}

interface LocationValue {
  lat: number;
  lng: number;
  address: Address | undefined | null;
}

interface LocationPickerProps {
  value?: LocationValue;
  onLocationChange?: (location: LocationValue) => void;
  height?: string;
  defaultCenter?: { lat: number; lng: number };
  defaultZoom?: number;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onLocationChange,
  height = '350px',
  defaultCenter = { lat: 17.385, lng: 78.4867 },
  defaultZoom = 12,
}) => {
  // Refs
  const mapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const locationRef = useRef<LocationValue | null>(null);
  const [isApiLoaded, setIsApiLoaded] = useState<boolean>(false);
  const snackbar = useSnackbar();

  // Reverse Geocoding
  const reverseGeocode = useCallback(
    async (lat: number, lng: number): Promise<Address | null> => {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`,
        );
        const data = await response.json();
        if (!data.results?.length) return null;

        const result = data.results[0];
        const addressComponents = result.address_components;

        const getComponent = (types: string[]) =>
          addressComponents.find((c: any) =>
            types.every((t) => c.types.includes(t)),
          )?.long_name || '';

        return {
          street: result.formatted_address,
          city:
            getComponent(['locality']) ||
            getComponent(['administrative_area_level_2']),
          state: getComponent(['administrative_area_level_1']),
          pincode: getComponent(['postal_code']),
        };
      } catch (error) {
        console.error('Reverse geocoding failed:', error);
        return null;
      }
    },
    [],
  );

  // Location Selection Handler
  const handleLocationSelect = useCallback(
    async (lat: number, lng: number) => {
      const address = await reverseGeocode(lat, lng);
      locationRef.current = { lat, lng, address: address || undefined };
    },
    [reverseGeocode],
  );

  const saveLocation = useCallback(() => {
    if (locationRef.current) {
      onLocationChange?.(locationRef.current);
    }
  }, [onLocationChange]);

  // Google Maps Script Loader
  const loadGoogleMapsScript = useCallback(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      console.error('Google Maps API key is missing');
      return;
    }

    if (window.google && window.google.maps) {
      setIsApiLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsApiLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps script');
    document.head.appendChild(script);
  }, []);

  // Map Event Listeners
  const setupMapListeners = useCallback(
    (map: google.maps.Map, marker: google.maps.Marker) => {
      // Click listener
      const clickListener = map.addListener(
        'click',
        (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          marker.setPosition(e.latLng);
          handleLocationSelect(lat, lng);
          inputRef.current && (inputRef.current.value = '');
        },
      );

      // Drag listener
      const dragListener = google.maps.event.addListener(
        marker,
        'dragend',
        (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          handleLocationSelect(lat, lng);
          inputRef.current && (inputRef.current.value = '');
        },
      );

      return () => {
        google.maps.event.removeListener(clickListener);
        google.maps.event.removeListener(dragListener);
      };
    },
    [handleLocationSelect],
  );

  // Autocomplete Setup
  const setupAutocomplete = useCallback(
    (map: google.maps.Map, marker: google.maps.Marker) => {
      if (!inputRef.current || !window.google) return;

      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: [
            'geometry',
            'formatted_address',
            'name',
            'address_components',
          ],
          componentRestrictions: { country: [] }, // Allow all countries
        },
      );

      autocomplete.bindTo('bounds', map);
      autocompleteRef.current = autocomplete;

      autocomplete.addListener('place_changed', async () => {
        const place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) return;

        const location = place.geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        map.panTo(location);
        map.setZoom(16);
        marker.setPosition(location);
        await handleLocationSelect(lat, lng);
      });

      return () => {
        if (autocompleteRef.current) {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        }
      };
    },
    [handleLocationSelect],
  );

  //  Current Location Handler
  const handleGetCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      snackbar.error('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        markerRef.current?.setPosition(
          new google.maps.LatLng(latitude, longitude),
        );
        mapInstanceRef.current?.panTo(
          new google.maps.LatLng(latitude, longitude),
        );
        mapInstanceRef.current?.setZoom(16);
        await handleLocationSelect(latitude, longitude);
      },
      (error) => {
        const messages = {
          1: 'Location permission denied. Please enable location access.',
          2: 'Location information is unavailable.',
          3: 'Location request timed out.',
        };
        snackbar.error(
          messages[error.code as 1 | 2 | 3] || 'Failed to get current location',
        );
        console.error('Geolocation error:', error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, [snackbar, reverseGeocode, onLocationChange]);

  // Sync external value
  const syncExternalValue = useCallback(() => {
    if (!value || !mapInstanceRef.current || !markerRef.current) return;

    const position = new google.maps.LatLng(value.lat, value.lng);
    markerRef.current.setPosition(position);
    mapInstanceRef.current.panTo(position);
    mapInstanceRef.current.setZoom(16);
  }, [value]);

  // ===== EFFECTS =====
  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (!isApiLoaded || !mapRef.current || mapInstanceRef.current) return;

    const initialCenter = value
      ? { lat: value.lat, lng: value.lng }
      : defaultCenter;
    const map = new google.maps.Map(mapRef.current, {
      center: initialCenter,
      zoom: defaultZoom,
    });
    const marker = new google.maps.Marker({
      position: initialCenter,
      map,
      draggable: true,
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    // Setup listeners
    const cleanupListeners = setupMapListeners(map, marker);
    const cleanupAutocomplete = setupAutocomplete(map, marker);

    return () => {
      cleanupListeners?.();
      cleanupAutocomplete?.();
      google.maps.event.clearInstanceListeners(map);
      google.maps.event.clearInstanceListeners(marker);
      mapInstanceRef.current = null;
      markerRef.current = null;
      autocompleteRef.current = null;
    };
  }, [
    isApiLoaded,
    defaultCenter,
    defaultZoom,
    setupMapListeners,
    setupAutocomplete,
    value,
  ]);

  useEffect(() => {
    syncExternalValue();
  }, [syncExternalValue]);

  if (!GOOGLE_MAPS_API_KEY) {
    console.error('Google Maps API key is missing');
  }

  if (!isApiLoaded) {
    return <LoadingState>Loading Google Maps...</LoadingState>;
  }

  return (
    <>
      <GlobalAutocompleteStyles />
      <MapContainer>
        <ControlsContainer>
          <SearchBox ref={inputRef} type="text" placeholder="Search location" />
          <CurrentLocationButton
            type="primary"
            icon={<AimOutlined />}
            onClick={handleGetCurrentLocation}
            size="large"
          >
            Use Current Location
          </CurrentLocationButton>
          <SaveLocationButton size="large" onClick={saveLocation}>
            Save Location
          </SaveLocationButton>
        </ControlsContainer>
        <MapBox ref={mapRef} style={{ height }} />
      </MapContainer>
    </>
  );
};

export type { Address, LocationValue };
export default LocationPicker;

const MapContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 400px;
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchBox = styled.input`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${COLORS.grey.secondary};
  font-size: ${TEXT_SIZES.MD}px;
  flex: 1;
  height: 40px;
  outline: none;
  &:focus {
    border-color: ${COLORS.blue.primary};
  }

  position: relative;
  z-index: 1;
`;

const CurrentLocationButton = styled(CustomButton)`
  white-space: nowrap;
`;

const SaveLocationButton = styled(CustomButton)`
  white-space: nowrap;
  background-color: ${COLORS.grey.primary} !important;
  color: ${COLORS.white.primary} !important;
`;

const MapBox = styled.div`
  border-radius: 4px;
  border: 1px solid ${COLORS.grey.secondary};
  flex: 1;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: ${COLORS.white.cardBackground};
  border-radius: 8px;
  color: ${COLORS.grey.primary};
  font-size: ${TEXT_SIZES.SM}px;
  width: 100%;
`;
